import { FilterPeriodState, FilterPeriodType } from '@shared/components/filter-period';
import { GroupedOption } from '@shared/components/group-select';
import { Option } from '@shared/components/select-new/Select';
import { SwitchType } from '@shared/components/switch';
import { LocationType, WipFeesType } from '@shared/types/common/submarket';
import { IndexResponse } from '@shared/types/services';
import { QueryFactory } from '@shared/utils/http-client';

import { FilterAutocompleteItemState } from './autocomplete';
import { Range } from './range';

export enum FilterType {
  date,
  dateRange,
  locationRadios,
  locationRegion,
  lookupAgency,
  lookupAgent,
  lookupCompany,
  lookupContact,
  lookupUser,
  multiGroupSelect,
  multiLookupAgent,
  multiLookupList,
  multiLookupCompany,
  multiLookupDisposal,
  multiSelect,
  numberFieldWithSingleSelect,
  range,
  requirementDate,
  singleGroupSelect,
  singleSelect,
  switch,
  text,
  textFieldWithSingleSelect,
}

export type FiltersTypeConfig<TFT = FilterType> = {
  label: string;
  labelCheckbox?: boolean;
  placeholder?: string;
  removable?: boolean;
  tooltip?: string;
  type: TFT;
  visible?: boolean;
};

interface FieldWithSelectFilterTypeConfig<TF extends FilterType, TO extends Option = Option>
  extends FiltersTypeConfig<TF> {
  fieldLabel?: string;
  options: TO[];
  selectLabel?: string;
}

interface SelectFilterTypeConfig<TF extends FilterType, TO extends Option = Option> extends FiltersTypeConfig<TF> {
  options: TO[];
}

interface DateFilterTypeConfig extends FiltersTypeConfig<FilterType.date> {
  supportedTypes?: FilterPeriodType[];
}

interface LocationRadiosFilterTypeConfig extends FiltersTypeConfig<FilterType.locationRadios> {
  defaultMetric?: Metric;
}

interface MultiGroupSelectFilterTypeConfig extends FiltersTypeConfig<FilterType.multiGroupSelect> {
  groupOptions: GroupedOption[];
}

interface RangeFilterTypeConfig extends FiltersTypeConfig<FilterType.range> {
  currency?: boolean;
  currencyOptions?: Option[];
  defaultCurrency?: string;
  defaultMetric?: Metric;
  metric?: boolean;
  metricOptions?: Option[];
}

interface SingleGroupSelectFilterTypeConfig extends FiltersTypeConfig<FilterType.singleGroupSelect> {
  groupOptions: GroupedOption[];
}

interface SwitchFilterTypeConfig extends FiltersTypeConfig<FilterType.switch> {
  switchType: SwitchType;
  condition?: boolean;
  subLabel?: string;
}

interface MultiLookupListFilterTypeConfig extends FiltersTypeConfig<FilterType.multiLookupList> {
  queryFactory: QueryFactory<IndexResponse<ItemWithId>>;
}

export type FiltersTypeConfigs =
  | DateFilterTypeConfig
  | FieldWithSelectFilterTypeConfig<FilterType.numberFieldWithSingleSelect>
  | FieldWithSelectFilterTypeConfig<FilterType.textFieldWithSingleSelect>
  | FiltersTypeConfig<FilterType.dateRange>
  | FiltersTypeConfig<FilterType.locationRegion>
  | FiltersTypeConfig<FilterType.lookupAgency>
  | FiltersTypeConfig<FilterType.lookupAgent>
  | FiltersTypeConfig<FilterType.lookupCompany>
  | FiltersTypeConfig<FilterType.lookupContact>
  | FiltersTypeConfig<FilterType.multiLookupAgent>
  | FiltersTypeConfig<FilterType.multiLookupCompany>
  | FiltersTypeConfig<FilterType.multiLookupDisposal>
  | FiltersTypeConfig<FilterType.lookupUser>
  | FiltersTypeConfig<FilterType.requirementDate>
  | FiltersTypeConfig<FilterType.text>
  | LocationRadiosFilterTypeConfig
  | MultiGroupSelectFilterTypeConfig
  | MultiLookupListFilterTypeConfig
  | RangeFilterTypeConfig
  | SingleGroupSelectFilterTypeConfig
  | SelectFilterTypeConfig<FilterType.multiSelect>
  | SelectFilterTypeConfig<FilterType.singleSelect>
  | SwitchFilterTypeConfig;

export enum AvailableFilters {
  advisoryStatus,
  advisoryType,
  agency,
  agent,
  amlOrKycCompleted,
  assignee,
  availableSpace,
  buildingGrade,
  buildingTypes,
  buildStatus,
  builtYear,
  bulkJobStatus,
  capVal,
  country,
  company,
  companySource,
  companyStatus,
  companyTags,
  companyType,
  completionDate,
  constructionStartYear,
  contact,
  contactStatus,
  contactTags,
  contactType,
  created,
  createdCrmCompanies,
  createdCrmContacts,
  createdDateRange,
  createdTransactionsSales,
  dateAdded,
  disposalDevelopmentCompletionYear,
  disposalDevelopmentFloorSize,
  disposalDevelopmentFunding,
  disposalDevelopmentLikelihood,
  disposalDevelopmentNoOfFloors,
  disposalDevelopmentPreLetCommitted,
  disposalDevelopmentSpeculative,
  disposalDevelopmentStatus,
  disposalDevelopmentTotalAreaProposed,
  disposalDevelopmentType,
  disposalFittedSpaces,
  disposalHasPresentation,
  disposalLeaseTypes,
  disposalPublishedTo,
  disposalSaleType,
  disposalStatus,
  disposalSharedWithJointAgents,
  disposalSharedWithLandlord,
  disposalTermsOfEngagement,
  disposalTags,
  disposalWipStatus,
  dueDate,
  externalOnly,
  emptyPropertyRecords,
  feeBasis,
  feeSplitTeams,
  feesStage,
  feesStatus,
  feesType,
  fitoutConcept,
  fitoutCondition,
  floorsAboveGround,
  floorsBelowGround,
  floorUnitOptions,
  gdprConsent,
  has_active_disposals,
  has_active_leases,
  has_active_requirements,
  has_active_sales,
  has_active_viewings,
  has_investor_profile,
  hasAvailability,
  hasLeases,
  hasOpenTasks,
  hasSales,
  inResearchStock,
  independentBuilding,
  internalConfidentialityDate,
  investmentGrade,
  investmentMarketStatus,
  investorAmountUnderManagement,
  investorNationality,
  investorRanking,
  investorRegion,
  investorType,
  invoice,
  jointExclusiveInstructions,
  kycCompleted,
  leadAgent,
  leadTeam,
  leaseActivityStatus,
  leaseEvent,
  leaseBreak,
  leaseEnd,
  leaseExpiry,
  leaseReminder,
  leaseStart,
  leaseTypes,
  lettingTransactionTags,
  location,
  locationRadios,
  lotSize,
  marketing_lists,
  marketing_permission,
  nationality,
  newMatches,
  niy,
  occupiedSpace,
  office,
  ourInstructions,
  ownershipTypes,
  parkingSpacesAboveGround,
  parkingSpacesBelowGround,
  pendingMatches,
  planningStatus,
  postcode,
  probabilityRange,
  propertyAddOnFactor,
  propertySize,
  publishedToAtlas,
  publishedToRadius,
  recordOwner,
  refurbYear,
  rentCurrency,
  rentReview,
  requirementType,
  internalConfidentiality,
  reverseSegmentIntersectLogic,
  riskProfile,
  salePrice,
  saleType,
  saleTransactionTags,
  sectors,
  shareWithWip,
  showSingleFloors,
  size,
  societyDisposalLeaseTypes,
  sources,
  startDate,
  tags,
  teams,
  tenancyStatus,
  tenure,
  tny,
  transactionDate,
  transactionStatus,
  typicalFloorSize,
  updated,
  user,
  vacantSpace,
  waultToExpiry,
  withoutDisposalTags,
  rent,
  publishedToSociety,
  fittedSpaces,
  knownTenant,
  price,
  showPendingMatch,
  status,
  feeSource,
  feeSourceDetailOwnActivity,
  feeSourceDetailCrossSellInternal,
  feeSourceDetailCrossSellExternal,
}

export type AvailableFiltersHash = { [key: number]: AvailableFilters };

export interface FieldWithSingleSelectState {
  field: string;
  select: Id;
}

export interface FilterLocationFieldState {
  [LocationType.postcode]?: string;
  [LocationType.submarkets]?: string[];
  [LocationType.teamSubmarkets]?: string[];
}

export interface FilterLocationRegionFieldState {
  [WipFeesType.city]?: string;
  [WipFeesType.country]?: string;
}

export interface FilterRangeState extends Range {
  metric?: Metric;
  currency?: string;
}

export interface FilterRequirementDateFieldState {
  [WipFeesType.due_date]?: Range;
  [WipFeesType.start_date]?: Range;
}

interface FilterStateValue<T = any> {
  checkboxValue?: boolean;
  value: T;
}

export type FilterStateValues = {
  [FilterType.date]: FilterStateValue<FilterPeriodState>;
  [FilterType.dateRange]: FilterStateValue<Range>;
  [FilterType.locationRadios]: FilterStateValue<FilterLocationFieldState>;
  [FilterType.locationRegion]: FilterStateValue<FilterLocationRegionFieldState>;
  [FilterType.lookupAgency]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.lookupAgent]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.lookupCompany]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.lookupContact]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.multiLookupAgent]: FilterStateValue<ItemWithId[]>;
  [FilterType.multiLookupList]: FilterStateValue<ItemWithId[]>;
  [FilterType.multiLookupCompany]: FilterStateValue<ItemWithId[]>;
  [FilterType.multiLookupDisposal]: FilterStateValue<ItemWithId[]>;
  [FilterType.lookupUser]: FilterStateValue<FilterAutocompleteItemState>;
  [FilterType.multiGroupSelect]: FilterStateValue<Id[]>;
  [FilterType.multiSelect]: FilterStateValue<Id[]>;
  [FilterType.numberFieldWithSingleSelect]: FilterStateValue<FieldWithSingleSelectState>;
  [FilterType.range]: FilterStateValue<FilterRangeState>;
  [FilterType.requirementDate]: FilterStateValue<FilterRequirementDateFieldState>;
  [FilterType.singleGroupSelect]: FilterStateValue<Id | undefined>;
  [FilterType.singleSelect]: FilterStateValue<Id | undefined>;
  [FilterType.switch]: FilterStateValue<boolean | undefined>;
  [FilterType.text]: FilterStateValue<string | undefined>;
  [FilterType.textFieldWithSingleSelect]: FilterStateValue<FieldWithSingleSelectState>;
};

export type AllFilterStateValues = ValueOf<FilterStateValues>;

type PartialFilterStateValue<T extends AllFilterStateValues = AllFilterStateValues> = FilterStateValue<
  Partial<T['value']>
>;

export type PartialFilterStateValues = {
  [FilterType.date]: PartialFilterStateValue<FilterStateValues[FilterType.date]>;
  [FilterType.dateRange]: PartialFilterStateValue<FilterStateValues[FilterType.dateRange]>;
  [FilterType.locationRadios]: PartialFilterStateValue<FilterStateValues[FilterType.locationRadios]>;
  [FilterType.locationRegion]: PartialFilterStateValue<FilterStateValues[FilterType.locationRegion]>;
  [FilterType.lookupAgency]: PartialFilterStateValue<FilterStateValues[FilterType.lookupCompany]>;
  [FilterType.lookupAgent]: PartialFilterStateValue<FilterStateValues[FilterType.lookupAgent]>;
  [FilterType.lookupAgent]: PartialFilterStateValue<FilterStateValues[FilterType.lookupCompany]>;
  [FilterType.lookupCompany]: PartialFilterStateValue<FilterStateValues[FilterType.lookupCompany]>;
  [FilterType.lookupContact]: PartialFilterStateValue<FilterStateValues[FilterType.lookupContact]>;
  [FilterType.lookupUser]: PartialFilterStateValue<FilterStateValues[FilterType.lookupUser]>;
  [FilterType.multiGroupSelect]: FilterStateValue<FilterStateValues[FilterType.multiGroupSelect]['value']>;
  [FilterType.multiLookupAgent]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupAgent]>;
  [FilterType.multiLookupList]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupList]>;
  [FilterType.multiLookupCompany]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupCompany]>;
  [FilterType.multiLookupDisposal]: PartialFilterStateValue<FilterStateValues[FilterType.multiLookupDisposal]>;
  [FilterType.multiSelect]: FilterStateValue<FilterStateValues[FilterType.multiSelect]['value']>;
  [FilterType.numberFieldWithSingleSelect]: PartialFilterStateValue<
    FilterStateValues[FilterType.numberFieldWithSingleSelect]
  >;
  [FilterType.range]: PartialFilterStateValue<FilterStateValues[FilterType.range]>;
  [FilterType.requirementDate]: PartialFilterStateValue<FilterStateValues[FilterType.requirementDate]>;
  [FilterType.singleGroupSelect]: FilterStateValue<FilterStateValues[FilterType.singleGroupSelect]['value']>;
  [FilterType.singleSelect]: PartialFilterStateValue<FilterStateValues[FilterType.singleSelect]>;
  [FilterType.switch]: PartialFilterStateValue<FilterStateValues[FilterType.switch]>;
  [FilterType.text]: PartialFilterStateValue<FilterStateValues[FilterType.text]>;
  [FilterType.textFieldWithSingleSelect]: PartialFilterStateValue<
    FilterStateValues[FilterType.textFieldWithSingleSelect]
  >;
};
